import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Table, Pagination, Modal, Input, Select, DatePicker, Button, Form, Row } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';
import moment from 'moment';
import { api } from '@med-fe/util';
import { SuffixIcon, TagRenderer } from '@med-fe/ui';

import {
  getUserIsMODAdmin,
  getUserPositions,
  getReadTabsPermissions,
  getWriteTabsPermissions,
} from '../../store/user/user-reducer';
import { CALL_FORWARDING_AUTHORIZED_POSITIONS } from '../../constants/permissions.constants';
import { getCallForwardingColumns } from '../grid/grid-configs/call-forwarding-columns';
import {
  getCallForwardingData,
  getCallForwardingLoading,
  getCallForwardingCancelLoading,
  getCallForwardingTotal,
} from './selectors';
import { fetchCallForwarding, cancelCallForwarding } from './actions';
import styles from '../grid/BaseGrid.module.scss';

const { Text } = Typography;
const FILTER_SELECT_WIDTH = 260;
const FILTER_DATE_WIDTH = 150;

interface CallForwardingSnapshotProps {
  locId?: number;
}

function CallForwardingSnapshot({ locId }: CallForwardingSnapshotProps) {
  const dispatch = useDispatch();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [sourceOfficeDraftFilter, setSourceOfficeDraftFilter] = useState<string | undefined>(undefined);
  const [destinationOfficeDraftFilter, setDestinationOfficeDraftFilter] = useState<string | undefined>(undefined);
  const [statusDraftFilters, setStatusDraftFilters] = useState<string[]>(['Active', 'Scheduled']);
  const [startDateDraftFilter, setStartDateDraftFilter] = useState<moment.Moment | null>(null);
  const [endDateDraftFilter, setEndDateDraftFilter] = useState<moment.Moment | null>(null);

  const [sourceOfficeFilter, setSourceOfficeFilter] = useState<string | undefined>(undefined);
  const [destinationOfficeFilter, setDestinationOfficeFilter] = useState<string | undefined>(undefined);
  const [statusFilters, setStatusFilters] = useState<string[]>(['Active', 'Scheduled']);
  const [startDateFilter, setStartDateFilter] = useState<moment.Moment | null>(null);
  const [endDateFilter, setEndDateFilter] = useState<moment.Moment | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sourceOfficeOptions, setSourceOfficeOptions] = useState<{ value: string; label: string }[]>([]);
  const [destinationOfficeOptions, setDestinationOfficeOptions] = useState<{ value: string; label: string }[]>([]);
  const [sourceOfficeOptionsLoading, setSourceOfficeOptionsLoading] = useState(false);
  const [destinationOfficeOptionsLoading, setDestinationOfficeOptionsLoading] = useState(false);

  const data = useSelector(getCallForwardingData) || [];
  const total = useSelector(getCallForwardingTotal);
  const loading = useSelector(getCallForwardingLoading);
  const cancelLoading = useSelector(getCallForwardingCancelLoading);
  const userPositions = useSelector(getUserPositions);
  const userIsMODAdmin = useSelector(getUserIsMODAdmin);
  const isReadAllowed = useSelector(getReadTabsPermissions);
  const isWriteAllowed = useSelector(getWriteTabsPermissions);

  // Check if user has permission to view this tab (positions OR read permission)
  const hasViewPermission =
    userIsMODAdmin ||
    CALL_FORWARDING_AUTHORIZED_POSITIONS.some((pos) => userPositions?.includes(pos)) ||
    isReadAllowed.callForwarding ||
    isWriteAllowed.callForwarding;

  // Check if user has permission to perform write actions (positions OR write permission)
  const hasWritePermission =
    userIsMODAdmin ||
    CALL_FORWARDING_AUTHORIZED_POSITIONS.some((pos) => userPositions?.includes(pos)) ||
    isWriteAllowed.callForwarding;

  const effectiveStatuses = useMemo(() => {
    if (!statusFilters.length || statusFilters.includes('All')) {
      return [];
    }

    return statusFilters;
  }, [statusFilters]);

  const activeParam = useMemo(() => {
    // If status filter asks for anything beyond Active (or All), fetch from all records.
    return effectiveStatuses.length > 0 && effectiveStatuses.every((status) => status === 'Active');
  }, [effectiveStatuses]);

  // Fetch data from server based on current paging and applied filters.
  useEffect(() => {
    if (hasViewPermission) {
      dispatch(
        fetchCallForwarding({
          active: activeParam,
          page: currentPage - 1,
          pageSize,
          sourceOfficeNumber: sourceOfficeFilter,
          destinationOfficeNumber: destinationOfficeFilter,
          startDate: startDateFilter ? startDateFilter.format('YYYY-MM-DD') : undefined,
          endDate: endDateFilter ? endDateFilter.format('YYYY-MM-DD') : undefined,
          status: effectiveStatuses,
        })
      );
    }
  }, [
    dispatch,
    hasViewPermission,
    activeParam,
    currentPage,
    pageSize,
    sourceOfficeFilter,
    destinationOfficeFilter,
    startDateFilter,
    endDateFilter,
    effectiveStatuses,
  ]);

  const normalizedData = useMemo(
    () =>
      data.map((item: any) => ({
        ...item,
        SourceOfficeNumber: String(item.SourceOfficeNumber ?? item.sourceOfficeNumber ?? ''),
        SourceOffice: item.SourceOffice ?? item.sourceOffice ?? '',
        DestinationOfficeNumber: String(item.DestinationOfficeNumber ?? item.destinationOfficeNumber ?? ''),
        DestinationOffice: item.DestinationOffice ?? item.destinationOffice ?? '',
        Status: item.Status ?? item.status ?? '',
        StartDate: item.StartDate ?? item.startDate ?? null,
        EndDate: item.EndDate ?? item.endDate ?? null,
      })),
    [data]
  );

  const displayData = useMemo(() => {
    if (!effectiveStatuses.length) {
      return normalizedData;
    }

    const statuses = new Set(effectiveStatuses);

    return normalizedData.filter((item: any) => statuses.has(item.Status));
  }, [effectiveStatuses, normalizedData]);

  const mapSourceOfficeOptions = (items: any[]) => {
    const unique = new Map<string, string>();

    items.forEach((item: any) => {
      const officeNumber = String(item.SourceOfficeNumber ?? item.sourceOfficeNumber ?? '').trim();
      const officeName = String(item.SourceOffice ?? item.sourceOffice ?? '').trim();

      if (!officeNumber) {
        return;
      }

      unique.set(officeNumber, officeName);
    });

    return Array.from(unique.entries()).map(([value, name]) => ({
      value,
      label: name ? `${value} - ${name}` : value,
    }));
  };

  const mapDestinationOfficeOptions = (items: any[]) => {
    const unique = new Map<string, string>();

    items.forEach((item: any) => {
      const officeNumber = String(item.DestinationOfficeNumber ?? item.destinationOfficeNumber ?? '').trim();
      const officeName = String(item.DestinationOffice ?? item.destinationOffice ?? '').trim();

      if (!officeNumber) {
        return;
      }

      unique.set(officeNumber, officeName);
    });

    return Array.from(unique.entries()).map(([value, name]) => ({
      value,
      label: name ? `${value} - ${name}` : value,
    }));
  };

  const fetchInitialOfficeOptions = async () => {
    setSourceOfficeOptionsLoading(true);
    setDestinationOfficeOptionsLoading(true);
    try {
      const response: any = await api.get(`/locations/call-forwarding-requests?active=false&page=0&pageSize=10000`);
      const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];

      setSourceOfficeOptions(mapSourceOfficeOptions(rows));
      setDestinationOfficeOptions(mapDestinationOfficeOptions(rows));
    } catch {
      setSourceOfficeOptions([]);
      setDestinationOfficeOptions([]);
    } finally {
      setSourceOfficeOptionsLoading(false);
      setDestinationOfficeOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (hasViewPermission) {
      fetchInitialOfficeOptions();
    }
  }, [hasViewPermission]);

  const statusOptions = useMemo(
    () => [
      { value: 'All', label: 'All' },
      { value: 'Active', label: 'Active' },
      { value: 'Scheduled', label: 'Scheduled' },
      { value: 'Cancelled', label: 'Cancelled' },
      { value: 'Expired', label: 'Expired' },
    ],
    []
  );

  const getStatusTagValue = (status?: string) => {
    const statusTagValueMap: Record<string, string> = {
      Active: 'active',
      Scheduled: 'pending',
      Cancelled: 'canceled',
      Expired: 'closed',
    };

    return status ? statusTagValueMap[status] || status.toLowerCase() : '';
  };

  const tableData = displayData.map((item: any, index: number) => ({
    ...item,
    key: item.Id || item.id || index,
  }));

  const handleSearchClick = () => {
    const selectedStatuses = statusDraftFilters.includes('All') ? ['All'] : statusDraftFilters;

    setSourceOfficeFilter(sourceOfficeDraftFilter);
    setDestinationOfficeFilter(destinationOfficeDraftFilter);
    setStatusFilters(selectedStatuses);
    setStartDateFilter(startDateDraftFilter);
    setEndDateFilter(endDateDraftFilter);
    setCurrentPage(1);
  };

  const handleResetClick = () => {
    setSourceOfficeDraftFilter(undefined);
    setDestinationOfficeDraftFilter(undefined);
    setStatusDraftFilters(['Active', 'Scheduled']);
    setStartDateDraftFilter(null);
    setEndDateDraftFilter(null);

    setSourceOfficeFilter(undefined);
    setDestinationOfficeFilter(undefined);
    setStatusFilters(['Active', 'Scheduled']);
    setStartDateFilter(null);
    setEndDateFilter(null);
    setCurrentPage(1);
  };

  const handlePaginationChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    }
  };

  const handleCancelClick = (record: any) => {
    setSelectedRecord(record);
    setCancellationReason('');
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = () => {
    if (selectedRecord) {
      const requestId = selectedRecord.Id || selectedRecord.id;
      dispatch(
        cancelCallForwarding(requestId, cancellationReason.trim(), {
          active: activeParam,
          page: currentPage - 1,
          pageSize,
          sourceOfficeNumber: sourceOfficeFilter,
          destinationOfficeNumber: destinationOfficeFilter,
          startDate: startDateFilter ? startDateFilter.format('YYYY-MM-DD') : undefined,
          endDate: endDateFilter ? endDateFilter.format('YYYY-MM-DD') : undefined,
          status: effectiveStatuses,
        })
      );
    }
    setCancelModalOpen(false);
    setSelectedRecord(null);
    setCancellationReason('');
  };

  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
    setSelectedRecord(null);
    setCancellationReason('');
  };

  const columns = getCallForwardingColumns(handleCancelClick, cancelLoading, hasWritePermission);

  if (!hasViewPermission) {
    return (
      <NoPermissionContainer>
        <Text>You do not have permission to view this content.</Text>
      </NoPermissionContainer>
    );
  }

  return (
    <Container>
      <CancelModalStyles />
      <CallForwardingFilterStyles />
      <Form
        layout='vertical'
        className='search-container location-search call-forwarding-search'
        onFinish={handleSearchClick}
        style={{ justifyContent: 'space-between' }}
      >
        <div style={{ display: 'flex', alignSelf: 'flex-start', flexDirection: 'column' }}>
          <Row wrap={true}>
            <Form.Item label='Select Source Office' className='label-text cf-select-field cf-source-office-field'>
              <Select
                allowClear
                value={sourceOfficeDraftFilter}
                onChange={setSourceOfficeDraftFilter}
                options={sourceOfficeOptions}
                loading={sourceOfficeOptionsLoading}
                suffixIcon={<SuffixIcon />}
                placeholder='Select Source Office'
                style={{ width: FILTER_SELECT_WIDTH }}
              />
            </Form.Item>
            <Form.Item
              label='Select Destination Office'
              className='label-text cf-select-field cf-destination-office-field'
            >
              <Select
                allowClear
                value={destinationOfficeDraftFilter}
                onChange={setDestinationOfficeDraftFilter}
                options={destinationOfficeOptions}
                loading={destinationOfficeOptionsLoading}
                suffixIcon={<SuffixIcon />}
                placeholder='Select Destination Office'
                style={{ width: FILTER_SELECT_WIDTH }}
              />
            </Form.Item>
            <Form.Item label='Select Start Date' className='label-text cf-date-field cf-start-date-field'>
              <DatePicker
                allowClear
                value={startDateDraftFilter}
                onChange={setStartDateDraftFilter}
                format='YYYY-MM-DD'
                style={{ width: FILTER_DATE_WIDTH }}
              />
            </Form.Item>
            <Form.Item label='Select End Date' className='label-text cf-date-field'>
              <DatePicker
                allowClear
                value={endDateDraftFilter}
                onChange={setEndDateDraftFilter}
                format='YYYY-MM-DD'
                style={{ width: FILTER_DATE_WIDTH }}
              />
            </Form.Item>
            <Form.Item label='Select Status' className='label-text cf-status-field'>
              <Select
                mode='multiple'
                allowClear
                value={statusDraftFilters}
                maxTagCount='responsive'
                suffixIcon={<SuffixIcon />}
                tagRender={(props) => <TagRenderer {...props} value={getStatusTagValue(String(props.value))} />}
                onChange={(values: string[]) => {
                  if (values.includes('All')) {
                    setStatusDraftFilters(['All']);
                    return;
                  }

                  setStatusDraftFilters(values.filter((value) => value !== 'All'));
                }}
                options={statusOptions}
                placeholder='Select Status'
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Row>
        </div>
        <div style={{ display: 'flex', marginTop: '10px', alignSelf: 'flex-start' }}>
          <Form.Item className='search-btn'>
            <Input.Group compact style={{ display: 'flex' }}>
              <Button type='primary' htmlType='submit'>
                SEARCH
              </Button>
              <Button onClick={handleResetClick} style={{ marginLeft: 8 }}>
                RESET
              </Button>
            </Input.Group>
          </Form.Item>
        </div>
      </Form>
      <div className={styles['table-container']}>
        <div className={styles['table-wrapper']}>
          <Table
            className={styles['table-common']}
            dataSource={tableData}
            columns={columns}
            pagination={false}
            loading={loading}
            scroll={{ x: '100%', y: 'calc(100vh - 20em)' }}
          />
        </div>
        <div className={styles['pagination-container']}>
          <div className={styles['pagination-actions-wrapper']}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              pageSizeOptions={['15', '25', '50', '100']}
              showSizeChanger={true}
              total={total}
              showTotal={(rowsTotal) => (
                <div className={styles['total-container']}>
                  <div className={styles['total-title']}>
                    <span>TOTAL COUNT</span>
                    <span className={styles['total-count']}>{rowsTotal}</span>
                  </div>
                </div>
              )}
              onChange={handlePaginationChange}
              onShowSizeChange={handlePaginationChange}
            />
          </div>
          <img alt='logo' className={styles['logo']} src={'./images/logo.svg'} />
        </div>
      </div>
      <Modal
        title='Cancel Call Forwarding Only Request Confirmation'
        okText='Ok'
        cancelText='Cancel'
        centered
        style={{ top: 20 }}
        open={cancelModalOpen}
        onOk={handleCancelConfirm}
        onCancel={handleCancelModalClose}
      >
        <span>Add cancellation reason or notes (optional), then click Ok to cancel this request.</span>
        <Input.TextArea
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
          placeholder='Enter cancellation reason or notes'
          rows={4}
          maxLength={1000}
          style={{ marginTop: 12 }}
        />
      </Modal>
    </Container>
  );
}

export default CallForwardingSnapshot;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const NoPermissionContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const CancelModalStyles = createGlobalStyle`
  .call-forwarding-cancel-modal .ant-modal {
    width: 980px !important;
    max-width: calc(100vw - 32px);
  }

  .call-forwarding-cancel-modal .ant-modal-body {
    min-height: 220px;
  }
`;

const CallForwardingFilterStyles = createGlobalStyle`
  .call-forwarding-search.ant-form .ant-row {
    column-gap: 16px;
    row-gap: 10px;
  }

  .call-forwarding-search.ant-form .ant-form-item {
    margin-right: 0;
  }

  .call-forwarding-search.ant-form .cf-select-field {
    width: ${FILTER_SELECT_WIDTH}px;
  }

  .call-forwarding-search.ant-form .cf-date-field {
    width: ${FILTER_DATE_WIDTH}px;
  }

  .call-forwarding-search.ant-form .cf-status-field {
    min-width: ${FILTER_SELECT_WIDTH}px;
  }

  .call-forwarding-search.ant-form .cf-status-field .ant-select-selection-overflow {
    flex-wrap: nowrap;
  }

  .call-forwarding-search.ant-form .cf-source-office-field,
  .call-forwarding-search.ant-form .cf-destination-office-field,
  .call-forwarding-search.ant-form .cf-start-date-field {
    margin-right: 8px;
  }
`;
